// Context provider that lets any descendent of the planner page open
// the create slide-over with a given kind + seed. Mirrors the Drillcut
// PlannerCreateGlobalProvider — single shared slide-over instance,
// callable from cards, command palette, etc.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { PlannerCreateSlideOver } from "./PlannerCreateSlideOver";
import type { CreateKind, CreateSeed } from "./createSlideOver/types";

interface OpenCreateFn {
  (kind: CreateKind, seed?: CreateSeed): void;
}

interface CreateCtx {
  openCreate: OpenCreateFn;
  /** Monotonic refresh counter — every successful create increments it.
   *  Views opt into auto-refresh by including this in their useResource
   *  deps array so the list refetches without a manual button click. */
  refreshKey: number;
}

const PlannerCreateContext = createContext<CreateCtx | null>(null);

export function PlannerCreateProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<CreateKind>("rock");
  const [seed, setSeed] = useState<CreateSeed | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const openCreate = useCallback<OpenCreateFn>((k, s) => {
    setKind(k);
    setSeed(s);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openCreate, refreshKey }), [openCreate, refreshKey]);

  return (
    <PlannerCreateContext.Provider value={value}>
      {children}
      <PlannerCreateSlideOver
        open={open}
        kind={kind}
        seed={seed}
        onClose={() => setOpen(false)}
        onKindChange={setKind}
        onCreated={() => {
          setOpen(false);
          setRefreshKey((n) => n + 1);
        }}
      />
    </PlannerCreateContext.Provider>
  );
}

export function usePlannerCreate(): OpenCreateFn {
  const ctx = useContext(PlannerCreateContext);
  if (!ctx) return () => undefined;
  return ctx.openCreate;
}

export function usePlannerRefreshKey(): number {
  const ctx = useContext(PlannerCreateContext);
  return ctx?.refreshKey ?? 0;
}
